import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { useSetRandomTallyInCart, useUpdateCart } from '~/api';
import { useGetSellerPriceForBuyer } from '~/api/inventory';
import { useCannedTallyVehicleId } from '~/api/canned-tally';
import { usePickedProductLength } from '~/hooks/usePickedProductLength';
import { Button } from '~/components/ui/buttons/Button';
import { WarningBanner } from '~/components/ui/Banner';
import { ProductLengthQuantityFields } from '~/components/common/ProductLengthQuantityFields';
import { useProductLengthPick } from '~/components/common/useProductLengthPick';
import {
  saveCompletePick,
  type CannedTallyOption,
  type CompleteLengthPick,
  type ProductLengthPick,
} from '~/components/common/productLengthChoice';
import type { LogisticsIncoterm, Quantity } from '~/graphql/graphql';
import {
  formatQuantityCell,
  formatUnitPrice,
  priceUnitLabel,
  toastGraphQLError,
} from '~/utils';
import { UNIT_PRICE_MAX_DECIMALS } from '~/utils/unitPrice';
import { MAX_PRICE, parsePriceInput } from '~/utils/moneyBounds';
import { PriceEditControl } from '~/components/common/PriceEditControl';
import { buildCartItemUpdateInput, type BundledLine } from '~/utils/order';
import { useCatalogAddPrice } from './useCatalogAddPrice';
import {
  findSavedCannedTallyOfProduct,
  findSavedTally,
  type SavedTallyKey,
} from '~/components/common/savedTallies';
import {
  hasPartialSavedRandomBundles,
  savedCannedTallyCount,
  savedRandomTallyBundles,
} from './cartTallyPreload';

export type CartLine = BundledLine & {
  platformProductsLengths: {
    id: string;
    length?: { id: string } | null;
  };
  quantity: Quantity;
  tallyPlatformProduct?: { id: string } | null;
  cannedTally?: { id: string; name: string } | null;
};

type CartCatalogAddRowProps = {
  hasSuggestions: boolean;
  cartId: string;
  cartItems: CartLine[];
  locationId: string;
  sellerOrganizationId: string;
  sellerOrganizationFacilityId: string;
  transportVehicleId?: string | null;
  parentOrderId?: string | null;
  incoTerm?: LogisticsIncoterm | null;
  cartCurrency?: string | null;
  sellerDefaultCurrency?: string;
  hasShippingRoute?: boolean;
};

type PriceDisplayProps = {
  price: number | null;
  unit: string;
  currency?: string;
  isLoading: boolean;
  isError: boolean;
  pricedOnAdd: boolean;
};

function PriceDisplay({
  price,
  unit,
  currency,
  isLoading,
  isError,
  pricedOnAdd,
}: Readonly<PriceDisplayProps>) {
  const { t } = useTranslation(['cart', 'order-builder']);
  if (isLoading) {
    return (
      <span
        className="text-sm italic text-neut-sec"
        data-testid="cart-catalog-add-price-loading"
      >
        {t('cart.catalog_price_loading')}
      </span>
    );
  }
  if (price == null && isError) {
    return (
      <span
        className="text-sm text-error-700"
        data-testid="cart-catalog-add-price-error"
      >
        {t('cart.catalog_price_error')}
      </span>
    );
  }
  if (price == null && pricedOnAdd) {
    return (
      <span
        className="text-sm font-semibold italic text-neut-sec"
        data-testid="cart-catalog-add-price"
      >
        {t('cart.catalog_priced_on_add')}
      </span>
    );
  }
  if (price == null) {
    return (
      <span className="flex flex-col">
        <span
          className="text-sm font-semibold italic text-neut-sec"
          data-testid="cart-catalog-add-price"
        >
          {t('cart.new_cart.no_price')}
        </span>
        <span
          className="text-xs text-neut-sec"
          data-testid="cart-catalog-add-price-enter-hint"
        >
          {t('order-builder:order_builder.table.no_listed_price_enter_price')}
        </span>
      </span>
    );
  }
  return (
    <span
      className="text-sm text-neut-prim"
      data-testid="cart-catalog-add-price"
    >
      {formatUnitPrice(price, currency)} /{priceUnitLabel(unit)}
    </span>
  );
}

type SavedTallyNotice =
  | { kind: 'canned-replace' }
  | { kind: 'other-canned-replace'; savedTallyName: string }
  | { kind: 'random-replace' };

function cartTallyKey(line: CartLine): SavedTallyKey | null {
  if (!line.tallyPlatformProduct) return null;
  return {
    platformProductId: line.tallyPlatformProduct.id,
    cannedTallyId: line.cannedTally ? line.cannedTally.id : null,
  };
}

function savedTallyNoticeFor(
  cartLines: CartLine[],
  pick: ProductLengthPick
): SavedTallyNotice | null {
  if (pick.kind === 'canned') {
    if (findSavedTally(cartLines, cartTallyKey, pick)) {
      return { kind: 'canned-replace' };
    }
    const otherCannedLine = findSavedCannedTallyOfProduct(
      cartLines,
      cartTallyKey,
      pick.platformProduct.id
    );
    return otherCannedLine && otherCannedLine.cannedTally
      ? {
          kind: 'other-canned-replace',
          savedTallyName: otherCannedLine.cannedTally.name,
        }
      : null;
  }
  return findSavedTally(cartLines, cartTallyKey, pick)
    ? { kind: 'random-replace' }
    : null;
}

export function CartCatalogAddRow({
  hasSuggestions,
  cartId,
  cartItems,
  locationId,
  sellerOrganizationId,
  sellerOrganizationFacilityId,
  transportVehicleId,
  parentOrderId,
  incoTerm,
  cartCurrency,
  sellerDefaultCurrency,
  hasShippingRoute,
}: Readonly<CartCatalogAddRowProps>) {
  const { t } = useTranslation(['cart', 'order-builder']);
  const queryClient = useQueryClient();
  const { mutate: updateCart, isPending: isUpdatingCart } = useUpdateCart();
  const { mutate: setRandomTallyInCart, isPending: isSettingRandomTally } =
    useSetRandomTallyInCart();
  const isPending = isUpdatingCart || isSettingRandomTally;
  const tallyVehicleId = useCannedTallyVehicleId(transportVehicleId);
  const { pick, fields, resetKey, reset } = useProductLengthPick({
    transportVehicleId: tallyVehicleId,
    cannedTallyOrganizationId: sellerOrganizationId,
  });
  const {
    pickedLengthId,
    existingItem: cartLine,
    replaceNoticeId,
  } = usePickedProductLength(
    pick,
    cartItems.filter((item) => !item.tallyPlatformProduct),
    (item) => item.platformProductsLengths.id
  );
  const isInTally =
    !!pickedLengthId &&
    cartItems.some(
      (item) =>
        item.tallyPlatformProduct &&
        item.platformProductsLengths.id === pickedLengthId
    );
  const {
    price: sellerPrice,
    isLoading: isPriceLoading,
    isError: isPriceError,
  } = useGetSellerPriceForBuyer(
    hasShippingRoute ? undefined : (pickedLengthId ?? undefined),
    sellerOrganizationId,
    sellerOrganizationFacilityId,
    incoTerm,
    locationId
  );
  const { display, priceForAdd, edit } = useCatalogAddPrice(
    pick,
    pickedLengthId,
    sellerPrice,
    !!cartLine
  );

  const savedTallyNotice = savedTallyNoticeFor(cartItems, pick);
  const existingCannedTallyCount = (cannedTally: CannedTallyOption) =>
    savedCannedTallyCount(cartItems, cannedTally);
  const hasPartialBundles =
    pick.kind === 'random' &&
    hasPartialSavedRandomBundles(cartItems, pick.platformProduct.id);
  const existingRandomBundles = useMemo(
    () => savedRandomTallyBundles(cartItems),
    [cartItems]
  );

  const isAddDisabled =
    pick.kind === 'length'
      ? !pick.isComplete || isPriceLoading || isPending || isInTally
      : !pick.isComplete || isPending;

  const onAdded = () => {
    toast.success(t('cart.catalog_add_success'));
    edit.reset();
    reset();
    void queryClient.invalidateQueries({
      queryKey: ['additionalItems', cartId],
    });
  };

  const addLength = (lengthPick: CompleteLengthPick) => {
    const forAdd = priceForAdd(lengthPick);
    if (forAdd.kind === 'unconvertible') {
      toast.error(
        t('order-builder:order_builder.add_to_cart_price_unconvertible', {
          unit: lengthPick.standardUnitsCode,
        })
      );
      return;
    }
    let suggestedPrice: number | null | undefined;
    if (forAdd.kind === 'send') suggestedPrice = forAdd.price;
    else if (forAdd.kind === 'clear') suggestedPrice = null;
    updateCart(
      {
        ...buildCartItemUpdateInput({
          productId: lengthPick.platformProductsLength.id,
          locationId,
          facilityId: sellerOrganizationFacilityId,
          quantity: lengthPick.quantity.value,
          unit: lengthPick.quantity.unit,
          parentOrderId,
        }),
        suggestedPrice,
      },
      { onSuccess: onAdded, onError: toastGraphQLError }
    );
  };

  const add = () =>
    saveCompletePick(pick, {
      length: addLength,
      canned: (cannedPick) =>
        updateCart(
          buildCartItemUpdateInput({
            productId: cannedPick.cannedTally.id,
            locationId,
            facilityId: sellerOrganizationFacilityId,
            quantity: cannedPick.tallyCount,
            unit: 'TALLIES',
            parentOrderId,
            isTally: true,
          }),
          { onSuccess: onAdded, onError: toastGraphQLError }
        ),
      random: (randomPick) =>
        setRandomTallyInCart(
          {
            sellerOrganizationFacilityId,
            locationId,
            platformProductId: randomPick.platformProduct.id,
            bundles: randomPick.bundles,
            ...(parentOrderId ? { parentOrderId } : {}),
          },
          { onSuccess: onAdded, onError: toastGraphQLError }
        ),
      lengths: () => {
        throw new Error('The cart row never ticks several lengths');
      },
    });

  const renderPriceSlot = () => {
    if (pick.kind !== 'length') {
      return (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-neut-sec">
            {t('cart.catalog_price')}
          </span>
          <p
            className="py-2 text-sm text-neut-sec"
            data-testid="cart-catalog-add-tally-price-hint"
          >
            {t(
              pick.kind === 'canned'
                ? 'cart.catalog_canned_tally_price_hint'
                : 'cart.catalog_random_tally_price_hint'
            )}
          </p>
        </div>
      );
    }
    if (!pick.platformProductsLength || !pick.quantity) {
      return (
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-neut-sec">
            {t('cart.catalog_price')}
          </span>
          <p
            className="py-2 text-sm text-neut-sec"
            data-testid="cart-catalog-add-price-hint"
          >
            {t('general:product_length_picker.quantity_hint')}
          </p>
        </div>
      );
    }
    return (
      <div className="relative flex flex-col gap-1">
        <span className="text-xs font-medium text-neut-sec">
          {t('cart.catalog_price')}
        </span>
        <div className="flex h-9 items-center gap-2">
          <PriceDisplay
            price={display.price}
            unit={display.unit}
            currency={
              sellerPrice?.currency ?? cartCurrency ?? sellerDefaultCurrency
            }
            isLoading={isPriceLoading}
            isError={isPriceError}
            pricedOnAdd={!!hasShippingRoute}
          />
          <PriceEditControl
            isEditing={edit.isEditing}
            isEdited={edit.isEdited}
            defaultValue={edit.defaultValue}
            onEditClick={edit.start}
            onSubmit={(value) => {
              const amount = parsePriceInput(
                value,
                UNIT_PRICE_MAX_DECIMALS
              ).amount;
              if (amount == null) {
                toast.error(
                  t('order-builder:order_builder.table.invalid_price', {
                    max: MAX_PRICE.toLocaleString('en-US'),
                  })
                );
                return;
              }
              edit.submit(amount);
            }}
            onCancel={edit.cancel}
            disabled={isPending}
            editTitle={t('order-builder:order_builder.table.edit_price_title')}
            editedTitle={t(
              'order-builder:order_builder.table.price_edited_title'
            )}
            editedAriaLabel={t(
              'order-builder:order_builder.table.price_edited_aria_label'
            )}
            testId="cart-catalog-add-price-edit"
          />
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-neut-sec">
        {t(
          hasSuggestions
            ? 'cart.catalog_add_caption'
            : 'cart.catalog_add_caption_empty'
        )}
      </p>
      <ProductLengthQuantityFields
        key={resetKey}
        testId="cart-catalog-add"
        fields={fields}
        existingCannedTallyCount={existingCannedTallyCount}
        existingRandomBundles={existingRandomBundles}
        disabled={isPending}
      >
        <div
          className="w-36 shrink-0"
          data-testid="cart-catalog-add-price-slot"
        >
          {renderPriceSlot()}
        </div>
        <div className="shrink-0 pt-5">
          <Button
            variant="primary"
            size="sm"
            width="auto"
            disabled={isAddDisabled}
            onClick={add}
            aria-describedby={
              cartLine || isInTally || savedTallyNotice || hasPartialBundles
                ? replaceNoticeId
                : undefined
            }
            data-testid="cart-catalog-add-submit"
          >
            <Plus size={14} aria-hidden="true" />
            {t('cart.catalog_add')}
          </Button>
        </div>
      </ProductLengthQuantityFields>
      <div id={replaceNoticeId} role="status">
        {savedTallyNotice && (
          <WarningBanner
            testId={`cart-catalog-add-${savedTallyNotice.kind}-notice`}
          >
            {savedTallyNotice.kind === 'other-canned-replace'
              ? t('cart.catalog_replaces_other_canned_tally', {
                  name: savedTallyNotice.savedTallyName,
                })
              : t(
                  savedTallyNotice.kind === 'canned-replace'
                    ? 'cart.catalog_replaces_canned_tally'
                    : 'cart.catalog_replaces_random_tally'
                )}
          </WarningBanner>
        )}
        {hasPartialBundles && (
          <WarningBanner testId="cart-catalog-add-partial-bundles-notice">
            {t('cart.catalog_random_tally_partial_bundles')}
          </WarningBanner>
        )}
        {isInTally && (
          <WarningBanner testId="cart-catalog-add-tally-notice">
            {t('cart.catalog_part_of_tally')}
          </WarningBanner>
        )}
        {cartLine && !isInTally && (
          <WarningBanner testId="cart-catalog-add-replace-notice">
            {t('cart.catalog_replaces_cart_quantity', {
              quantity: formatQuantityCell(
                cartLine.quantity.value,
                cartLine.quantity.unit
              ),
            })}
          </WarningBanner>
        )}
      </div>
    </div>
  );
}
